import { notification } from "antd";
import Card from "antd/es/card/Card";
import Processes from "entities/Processes";
import { isEmpty } from "lodash";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdUploadFile } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Header from "shared/components/Header";
import Search from "shared/components/Input";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { INITIAL_VALUES, SCREENS } from "utils/constants";
import ImportSummaryModal from "../Detail/ImportSummaryModal";
import View from "./View";

const List = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const CANVASES = t("CANVASES", { returnObjects: true });
  const duplicatingRef = useRef(false);
  const [duplicateProcess, { loading: duplicating }] = useMutation(
    graphql.mutations.DUPLICATE_PROCESS,
    {
      refetchQueries: [
        {
          query: graphql.queries.PROCESSES,
          awaitRefetchQueries: true,
          variables: {
            ...INITIAL_VALUES,
            like: isEmpty(search)
              ? undefined
              : { OR: [{ name: `%${search}%` }] },
          },
        },
      ],
      onCompleted: ({ duplicateProcess: copy }) => {
        duplicatingRef.current = false;
        navigate(`${SCREENS.PRIVATE.ADMIN.CANVASES.path}/${copy.id}`);
      },
      onError: () => {
        duplicatingRef.current = false;
        notification.error({ message: CANVASES.DUPLICATE_FAILED });
      },
    },
  );

  const onDuplicate = (id, name) => {
    if (duplicatingRef.current || !id) return;
    duplicatingRef.current = true;
    duplicateProcess({
      variables: {
        where: { id },
        data: { name },
      },
    });
  };

  const filter = {
    ...INITIAL_VALUES,
    like: isEmpty(search) ? undefined : { OR: [{ name: `%${search}%` }] },
  };

  const onImport = ({ target }) => {
    const file = target.files && target.files[0];
    if (file) setPendingFile(file);
    target.value = "";
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".yaml,.yml,.json"
        className="canvas-editor__hidden"
        onChange={onImport}
      />
      <ImportSummaryModal
        file={pendingFile}
        onClose={() => setPendingFile(null)}
        onImported={(id) =>
          navigate(`${SCREENS.PRIVATE.ADMIN.CANVASES.path}/${id}`)
        }
      />
      <Header
        title={CANVASES.TITLE}
        actions={[
          {
            text: CANVASES.IMPORT,
            onClick: () => fileInputRef.current && fileInputRef.current.click(),
            type: "primary",
            icon: <MdUploadFile size={20} />,
          },
        ]}
      />
      <Card type="simple">
        <div className="m-bottom-20 grid--4">
          <Search
            placeholder={CANVASES.SEARCH}
            type="search"
            onChange={({ target: { value } }) => {
              setPagination((prev) => ({ ...prev, skip: 0 }));
              setSearch(value);
            }}
          />
        </div>
        <Processes
          View={View}
          filter={{ ...filter, ...pagination }}
          setValues={setPagination}
          skip={pagination.skip || 0}
          NoData={View}
          duplicate={onDuplicate}
          duplicating={duplicating}
        />
      </Card>
    </>
  );
};

export default List;
